import * as Sentry from '@sentry/react';
import { startTransition, StrictMode } from 'react';
import { hydrateRoot } from 'react-dom/client';
import { HydratedRouter } from 'react-router/dom';

// Sentry: off without VITE_SENTRY_DSN, so dev builds never phone home.
// PII is stripped in beforeSend; errors only for now.
const dsn = import.meta.env.VITE_SENTRY_DSN;
if (dsn) {
	// `<stage>-fe` (dev-fe / prod-fe) so the frontends are distinct from the api
	// (`-be`) in Sentry. VITE_SENTRY_ENVIRONMENT overrides it at build time.
	const stage = import.meta.env.PROD ? 'prod' : 'dev';
	Sentry.init({
		dsn,
		environment: import.meta.env.VITE_SENTRY_ENVIRONMENT ?? `${stage}-fe`,
		tracesSampleRate: 0,
		sendDefaultPii: false,
		beforeSend: (event) => {
			if (event.user) {
				delete event.user.email;
				delete event.user.ip_address;
			}
			if (event.request?.headers) {
				delete event.request.headers.Cookie;
				delete event.request.headers.cookie;
			}
			return event;
		},
	});
}

// Dev-only accessibility check: axe-core scans the rendered page and
// logs any WCAG violations to the console. Runs on axe-core directly (works on
// any React version); dynamically imported so it never ships to prod.
if (import.meta.env.DEV) {
	void import('axe-core').then(({ default: axe }) => {
		setTimeout(() => {
			void axe.run(document.body).then((results) => {
				for (const violation of results.violations) {
					console.warn(`[axe] ${violation.id}: ${violation.help}`, violation.helpUrl);
				}
			});
		}, 1000);
	});
}

startTransition(() => {
	hydrateRoot(
		document,
		<StrictMode>
			<HydratedRouter />
		</StrictMode>,
	);
});
